import { TextBuffer } from "./helpers/buffer";
import { Editor } from "./models/Editor";
import { Action, IOperation } from "./types";

const INACTION_DELAY = 5000;

type State = string;

interface IChange {
	state: State;
	next: IChange;
	prev: IChange;
}

export class Manager {
	private _editor: Editor;
	private _changes: IChange;
	private _buffer: TextBuffer;

	private _inactionTimer;

	constructor(editor: Editor) {
		this._editor = editor;
		this.clearChanges();
		this._buffer = new TextBuffer(this._editor);
	}
	execute(operation: IOperation) {
		if (this._inactionTimer) {
			clearTimeout(this._inactionTimer);
			this._inactionTimer = null;
		}
		this._inactionTimer = setTimeout(() => {
			if (this._editor.selection.isActive()) {
				// if editor not in focus it can call some unexpected behaviour
				this._editor.balance();
			}
			this._inactionTimer = null;
		}, INACTION_DELAY);

		let result;

		switch (operation.action) {
			case Action.parse:
				result = this._editor.parse(operation.data.value, operation.data.mode);
				this.clearChanges();
				return true;
			case Action.add:
				result = this._editor.add(operation.data);
				break;
			case Action.innerAdd:
				if (typeof operation.data === "string") {
					result = this._editor.add(operation.data);
				} else {
					const { data, type } = operation.data;
					switch (type) {
						case "text":
							result = this._editor.add(data);
							break;
						case "full":
							result = this._editor.paste(JSON.stringify(data), "inner");
							break;
						case "textnode":
							// tricky
							result = this._editor.paste(
								JSON.stringify([
									{
										textNodes: [data],
										style: {},
									},
								]),
								"inner"
							);
							break;
					}
				}
				break;
			case Action.remove:
				result = this._editor.remove(operation.data);
				break;
			case Action.update:
				result = this._editor.update(operation.data);
				break;
			case Action.clear:
				result = this._editor.clearStyles();
				break;
			case Action.paste:
				result = this._buffer.paste(operation.data);
				break;
			case Action.cut:
				result = this._buffer.cut(operation.data);
				break;
			case Action.copy:
				this._buffer.copy(operation.data); // it split nodes, need to update view
				return true;
			case Action.selectAll:
				break;
		}
		if (result) {
			this._change();
			return true;
		}

		if (operation.action === Action.undo) {
			return this.undo();
		}

		if (operation.action === Action.redo) {
			return this.redo();
		}

		return false;
	}
	canUndo() {
		return this._changes.prev !== null;
	}
	canRedo() {
		return this._changes.next !== null;
	}
	undo() {
		const change = this._changes.prev;
		if (change) {
			this._editor.setState(change.state);
			this._changes = change;
			return true;
		}
		return false;
	}
	redo() {
		const change = this._changes.next;
		if (change) {
			this._editor.setState(change.state);
			this._changes = change;
			return true;
		}
		return false;
	}
	clearChanges() {
		this._changes = {
			prev: null,
			next: null,
			state: this._editor.getState(),
		};
	}
	private _change() {
		const newChange = {
			prev: this._changes,
			next: null,
			state: this._editor.getState(),
		};

		this._changes.next = newChange;
		this._changes = newChange;
	}
}
